import { checkClaimsWeb, EventrDate } from '@eventr/common'
import {
    AdminBobbin,
    ColoursObj,
    Gutters,
    Icon,
    MainButton,
    MenuButton,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { Paths } from '../../../routes'
import { bs, useBoundStore } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { useRouteGuardContext } from '../../../state/routeGuardProvider'
import { openBrowser } from '../../../utils'
import { ItinTagsList } from '../../components/itinTagsList'
import { ItineraryCard } from '../../components/itineraryCard'
import { NavigationView } from '../../layout/NavigationView'
import { navBarRef } from '../../layout/navBar'

type TItinInfo = {
    id: number
    name: string
    start: string
    end: string
}

export const ItineraryPage: FC = () => {
    const [pastList, setPastList] = useState<TItinInfo[]>([])
    const [currList, setCurrList] = useState<TItinInfo[]>([])
    const navbarHeight = navBarRef.value?.current?.clientHeight ?? 0
    const { isAllowed } = useRouteGuardContext()
    const { wpUser, itineraryMap, noItineraries, selectedItinID } =
        useBoundStore(
            useShallow((s) => ({
                wpUser: s.wpUser,
                itineraryMap: s.itineraryMap,
                noItineraries: s.noItineraries,
                selectedItinID: s.selectedItinID,
            }))
        )
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    useEffect(() => {
        if (itineraryMap.length > 0) {
            itineraryMap.forEach((itin) => {
                const startDate = new EventrDate(itin.start_time * 1000)
                const endDate = new EventrDate(itin.end_time * 1000)

                let timezone

                try {
                    const itinValue = JSON.parse(itin.value ?? '{}')

                    timezone =
                        itinValue?.OSMAddress?.timeZone ?? 'Europe/London'
                } catch {
                    timezone = 'Europe/London'
                }

                startDate.setTimezone(timezone, false)
                endDate.setTimezone(timezone, false)

                const startDateString = startDate.utcLuxon.toFormat(
                    'EEEE d MMMM yyyy',
                    {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    }
                )
                const endDateString = endDate.utcLuxon.toFormat(
                    'EEEE d MMMM yyyy',
                    {
                        locale: useLanguageContext
                            .getState()
                            .getLanguageISO639Code(),
                    }
                )

                const newItem = {
                    id: itin.id,
                    name: itin.itinerary_name,
                    start: startDateString,
                    end: endDateString,
                }

                const nowUnix = new Date().getTime()

                if (itin.end_time * 1000 < nowUnix) {
                    const isInPastList = pastList.some(
                        (item) => item.id === itin.id
                    )
                    if (!isInPastList) setPastList((list) => [...list, newItem])
                } else {
                    const isInCurrList = currList.some(
                        (item) => item.id === itin.id
                    )
                    if (!isInCurrList) setCurrList((list) => [...list, newItem])
                }
            })
        }
    }, [])

    /**
     * This needs to be made a seperate function....
     */
    async function onDelete(itinId: number) {
        await bs().deleteItinerary(itinId)

        clearDialog()
    }

    const itinEntry = (item: TItinInfo) => {
        const itineObj = itineraryMap.find((itin) => {
            return itin.id === item.id
        })

        if (!itineObj) return null

        const coverImg = JSON.parse(itineObj.value ?? '{}').cover_image

        return (
            <ItineraryCard
                key={item.id}
                imageUrl={coverImg}
                onClick={async () => {
                    if (wpUser) {
                        await bs().setCurrentItin(item.id)
                        window.pushPath(Paths.home)
                    }
                }}
                title={
                    <Typography
                        id="account-itineraries-itinerary-name"
                        color={ColoursObj.shades.shade5}
                    >
                        {item.name}
                    </Typography>
                }
                actions={
                    checkClaimsWeb(
                        [`/${selectedItinID}`],
                        ['deleteItinerary']
                    ) && (
                        <Box
                            id="binIcon"
                            display={'grid'}
                            justifyItems={'center'}
                            alignItems={'center'}
                            padding={'0 16px 0 0'}
                            onClick={() => {
                                replaceDialog({
                                    node: (
                                        <MobileConfirmDialog
                                            header={i18n.t(
                                                'edit-itinerary.section-selection.delete-itin'
                                            )}
                                            secondaryText={item.name}
                                            text={i18n.t(
                                                'edit-itinerary.section-selection.delete-itin-check'
                                            )}
                                            onConfirm={() => {
                                                onDelete(item.id)
                                            }}
                                            onCancel={() => {
                                                clearDialog()
                                            }}
                                            cancelText={i18n.t(
                                                'general.cancel'
                                            )}
                                            confirmText={i18n.t(
                                                'general.delete'
                                            )}
                                            confirmBtnStyle="destructiveBorderless"
                                        />
                                    ),
                                    backdropClosable: false,
                                })
                            }}
                        >
                            <Icon
                                name="Delete"
                                iconColour={ColoursObj.shades.shade5}
                            />
                        </Box>
                    )
                }
                description={
                    <>
                        <Typography
                            id="account-itineraries-itinerary-date-range"
                            variant="bodySubtext"
                            color={ColoursObj.shades.shade5}
                        >
                            {item.start === item.end
                                ? item.start
                                : [item.start, item.end].join(' - ')}
                        </Typography>
                        <ItinTagsList tagIds={itineObj.tags} />
                    </>
                }
            />
        )
    }

    return (
        <NavigationView
            navigationBarTitle={i18n.t('account.itineraries')}
            backButtonAction={() => {
                window.goBack()
            }}
            usePageTransition
        >
            <Gutters>
                <Box>
                    {noItineraries ? (
                        <Stack
                            alignItems={'center'}
                            justifyContent={'center'}
                            gap={'24px'}
                            height={`calc(85vh - ${navbarHeight}px)`}
                        >
                            <Typography
                                id="itineraryPageNoItinerariesSubText"
                                variant="mobileH3"
                            >
                                {i18n.t('account.no-itineraries')}
                            </Typography>
                            <AdminBobbin />
                            {isAllowed(['allowedCreateNewItin']) && (
                                <Box
                                    display={'grid'}
                                    gap={'24px'}
                                    alignItems={'center'}
                                    justifyItems={'center'}
                                    width={'100%'}
                                >
                                    <Typography
                                        color={ColoursObj.shades.shade1}
                                    >
                                        {i18n.t('account.create-one')}
                                    </Typography>
                                    <Link
                                        to={Paths.newItinerary}
                                        style={{ width: '100%' }}
                                    >
                                        <MainButton style={{ width: '100%' }}>
                                            <Stack
                                                flexDirection={'row'}
                                                gap="8px"
                                                alignItems={'center'}
                                            >
                                                <Icon
                                                    name="Add"
                                                    iconColour={
                                                        ColoursObj.shades.shade5
                                                    }
                                                    height="16px"
                                                    width="16px"
                                                />
                                                <Typography
                                                    fontSize={'16px'}
                                                    id={'new-itin'}
                                                    color={
                                                        ColoursObj.shades.shade5
                                                    }
                                                >
                                                    {i18n.t(
                                                        'account.create-new-itinerary'
                                                    )}
                                                </Typography>
                                            </Stack>
                                        </MainButton>
                                    </Link>
                                    <MainButton
                                        style={{ width: '100%' }}
                                        id="linkToPublicItineraries"
                                        onClick={() => {
                                            openBrowser(
                                                'https://eventr.softpauer.com/upcoming-events/'
                                            )
                                        }}
                                        icon={'Leave_App'}
                                        iconColour={ColoursObj.shades.shade5}
                                        iconPosition="left"
                                    >
                                        <Typography
                                            variant="bodyRegular"
                                            color={ColoursObj.shades.shade5}
                                        >
                                            {i18n.t('account.publicEvents')}
                                        </Typography>
                                    </MainButton>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Stack gap="8px">
                            {currList.length > 0 && (
                                <>
                                    <MenuButton
                                        style={{
                                            background:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                        }}
                                    >
                                        <Typography variant="bodyBold">
                                            {i18n.t('general.current')}
                                        </Typography>
                                    </MenuButton>

                                    {currList.map((item) => {
                                        return itinEntry(item)
                                    })}
                                </>
                            )}
                            {pastList.length > 0 && (
                                <>
                                    <MenuButton
                                        style={{
                                            background:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                        }}
                                    >
                                        <Typography variant="bodyBold">
                                            {i18n.t('general.past')}
                                        </Typography>
                                    </MenuButton>
                                    {pastList.map((item) => {
                                        return itinEntry(item)
                                    })}
                                </>
                            )}
                        </Stack>
                    )}
                </Box>
            </Gutters>
        </NavigationView>
    )
}
